// src/pages/WatchlistsPage.jsx
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PlusCircle, Trash2, Eye } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function WatchlistsPage() {
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);

  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");

  const firma = user?.firma || "LEMON";
  const pharmacyCode = user?.pharmacyCode || null;

  const load = async () => {
    if (!pharmacyCode) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/watchlists/${firma}/${pharmacyCode}`, {
        method: "GET",
        credentials: "include",
      });

      if (!res.ok) throw new Error();

      const json = await res.json();
      setData(json);
    } catch (e) {
      console.error("Chyba při získávání watchlistů:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  const createWatchlist = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;

    try {
      const res = await fetch(`/api/watchlists/${firma}/${pharmacyCode}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email }),
      });

      if (!res.ok) throw new Error();

      setName("");
      setEmail("");
      load();
    } catch (e) {
      alert("Chyba při vytváření.");
    }
  };

  const deleteWatchlist = async (id) => {
    if (!window.confirm("Smazat tento watchlist?")) return;

    try {
      const res = await fetch(
        `/api/watchlists/${firma}/${pharmacyCode}/${id}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (!res.ok) throw new Error();

      load();
    } catch (e) {
      alert("Chyba při mazání.");
    }
  };

  const saveEdit = async (wl) => {
    try {
      const res = await fetch(
        `/api/watchlists/${firma}/${pharmacyCode}/${wl._id}`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: editName,
            email: editEmail,
          }),
        }
      );

      if (!res.ok) throw new Error();

      setEditId(null);
      load();
    } catch (e) {
      alert("Chyba při ukládání změn.");
    }
  };

  return (
    <div className="p-4">
      <h1 className="text-xl font-bold mb-4">Moje watchlisty</h1>

      {/* Formulář */}
      <form onSubmit={createWatchlist} className="flex gap-2 mb-4">
        <input
          type="text"
          placeholder="Název watchlistu"
          className="border p-2 flex-1"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <input
          type="email"
          placeholder="E-mail (volitelné)"
          className="border p-2 flex-1"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <button
          type="submit"
          className="bg-blue-600 text-white px-3 py-2 rounded flex items-center gap-1"
        >
          <PlusCircle size={18} /> Přidat
        </button>
      </form>

      {/* Tabulka */}
      {loading ? (
        <p>Načítám…</p>
      ) : data.length === 0 ? (
        <p>Nemáte žádné watchlisty.</p>
      ) : (
        <table className="w-full border text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2">Název</th>
              <th className="p-2">E-mail</th>
              <th className="p-2 text-center">Akce</th>
            </tr>
          </thead>
          <tbody>
            {data.map((wl) => (
              <tr key={wl._id} className="border-t">
                <td className="p-2">{wl.name}</td>
                <td className="p-2">{wl.email || "—"}</td>

                <td className="p-2 text-center flex items-center justify-center gap-3">
                  <Link
                    to={`/watchlists/${wl.firma}/${wl.pharmacyCode}/${wl._id}`}
                    className="text-blue-600 inline-flex items-center gap-1"
                  >
                    <Eye size={16} /> Detail
                  </Link>

                  {/* EDITACE */}
                  {editId === wl._id ? (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        saveEdit(wl);
                      }}
                      className="flex gap-2 items-center"
                    >
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="border p-1 w-36 text-sm"
                      />
                      <input
                        type="email"
                        value={editEmail}
                        onChange={(e) => setEditEmail(e.target.value)}
                        className="border p-1 w-44 text-sm"
                      />

                      <button
                        type="submit"
                        className="bg-green-600 text-white px-2 py-1 rounded text-sm"
                      >
                        Uložit
                      </button>
                      <button
                        type="button"
                        className="bg-gray-300 px-2 py-1 rounded text-sm"
                        onClick={() => setEditId(null)}
                      >
                        Zrušit
                      </button>
                    </form>
                  ) : (
                    <button
                      className="text-yellow-600 inline-flex items-center gap-1"
                      onClick={() => {
                        setEditId(wl._id);
                        setEditName(wl.name);
                        setEditEmail(wl.email || "");
                      }}
                    >
                      ✏️ Upravit
                    </button>
                  )}

                  <button
                    className="text-red-600 inline-flex items-center gap-1"
                    onClick={() => deleteWatchlist(wl._id)}
                  >
                    <Trash2 size={16} /> Smazat
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
